import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const movieSlug = searchQuery.trim();
      setSearchQuery('');
      setSearchOpen(false);
      navigate(`/search?q=${encodeURIComponent(movieSlug)}`);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 font-heading font-bold text-xl whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-movie-line text-background-50 text-lg"></i>
            </div>
            <span>麻花影院</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影片..."
                  className="w-48 px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-500 transition-colors"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="ml-2 w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-foreground-600"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer transition-colors"
                aria-label="搜索"
              >
                <i className="ri-search-line text-foreground-700 text-lg"></i>
              </button>
            )}
            <Link
              to="/about"
              className="text-sm text-foreground-600 hover:text-foreground-950 whitespace-nowrap cursor-pointer transition-colors"
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer transition-colors"
            aria-label="菜单"
          >
            <i className={`text-foreground-700 text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex items-center bg-background-100 rounded-full px-4 py-2">
              <i className="ri-search-line text-foreground-400 mr-2"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索影片..."
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder-foreground-400 outline-none"
              />
            </div>
          </form>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 bg-background-100 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
          <Link
            to="/about"
            className="block mt-4 text-sm text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
          >
            关于我们
          </Link>
        </div>
      )}
    </nav>
  );
}